import { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { resolveImg } from "@/lib/api";

export default function HeroCarousel({ slides, company }) {
  const [index, setIndex] = useState(0);
  const count = 1; // carousel dezactivat: afișăm doar primul slide
  const phone = (company.phone_emergency || company.phone || "").replace(/\s/g, "");

  const go = useCallback(
    (dir) => setIndex((i) => (i + dir + count) % count),
    [count]
  );

  useEffect(() => {
    if (count <= 1) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % count), 5500);
    return () => clearInterval(t);
  }, [count]);

  if (!slides.length) return null;
  const slide = slides[index];

  return (
    <section
      data-testid="hero-carousel-container"
      className="relative overflow-hidden bg-[#0F1E36]"
    >
      <AnimatePresence mode="sync">
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="absolute inset-0"
        >
          <img
            src={resolveImg(slide.image_url)}
            alt={slide.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F1E36]/95 via-[#0F1E36]/75 to-[#0F1E36]/30" />
        </motion.div>
      </AnimatePresence>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
        <div className="max-w-2xl">
          <motion.p
            key={`tag-${index}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-xs font-bold uppercase tracking-[0.25em] text-white mb-4"
          >
            {slide.tag}
          </motion.p>
          <motion.h1
            key={`title-${index}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]"
          >
            {slide.title}
          </motion.h1>
          <motion.p
            key={`sub-${index}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 text-base sm:text-lg text-white/80 leading-relaxed max-w-xl"
          >
            {slide.subtitle}
          </motion.p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <a href="#contact" data-testid="hero-carousel-cta-primary">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-[#E11D2A] hover:bg-[#C01820] text-white font-semibold text-base px-7 h-13 py-6 shadow-lg shadow-[#E11D2A]/20"
              >
                Cere o Programare
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
            <a href={`tel:${phone}`} data-testid="hero-carousel-cta-secondary">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-white/30 bg-white/5 text-white hover:bg-white hover:text-[#0F1E36] font-semibold text-base px-7 py-6 backdrop-blur-sm"
              >
                <Phone className="w-5 h-5 mr-2" />
                {company.phone}
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Controls */}
      {count > 1 && (
        <>
          <button
            onClick={() => go(-1)}
            data-testid="hero-carousel-prev-button"
            aria-label="Slide anterior"
            className="hidden sm:flex absolute left-5 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-white/10 border border-white/20 text-white hover:bg-[#FF5A1F] hover:border-[#FF5A1F] transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => go(1)}
            data-testid="hero-carousel-next-button"
            aria-label="Slide următor"
            className="hidden sm:flex absolute right-5 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-white/10 border border-white/20 text-white hover:bg-[#FF5A1F] hover:border-[#FF5A1F] transition-colors duration-200"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2.5">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                data-testid={`hero-slide-indicator-${i}`}
                aria-label={`Slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === index ? "w-8 bg-[#FF5A1F]" : "w-4 bg-white/40 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
